/**
 * Teams and the round lifecycle.
 *
 * The server owns all of this; the client is told. Nothing here reads a clock —
 * every duration is a count of ticks, and the current phase is a pure function
 * of `tick`. That is deliberate and follows the same argument as `serverTimeMs`
 * in protocol.ts: the Workers runtime does not advance `Date.now()` freely
 * between I/O operations, so a wall-clock read inside the tick loop is not a
 * timeline anything can be built on. Counting ticks also makes the whole
 * lifecycle testable without faking time.
 *
 * Phase order, and why each exists:
 *
 *   Warmup   the lobby. Waiting for enough humans to make a round mean
 *            anything, and then for the host to say go — or for
 *            WARMUP_AUTOSTART_TICKS to say it for them. Movement is free;
 *            nobody is on a team yet.
 *   Hiding   hiders scatter and disguise. Hunters are frozen — this is the
 *            entire asymmetry the game is built on, and it is enforced on the
 *            server rather than by asking the client not to move.
 *   Hunting  hunters released. Ends when the hiders are all found or the clock
 *            runs out, whichever comes first.
 *   Over     the scoreboard beat. Exists so a round does not end and restart in
 *            the same frame, which reads as a bug rather than a result.
 *
 * Then teams swap and it begins again, because being hunted and doing the
 * hunting are different games and a session should contain both.
 */

import { Btn, type InputFrame } from "./movement.js";
import { TICK_RATE } from "./rules.js";

// ---------------------------------------------------------------------------
// Teams
// ---------------------------------------------------------------------------

/**
 * Wire values, so these are append-only — a snapshot carries the number.
 *
 * `Unassigned` is not "no team yet" as a null would be; it is a real state a
 * player occupies during Warmup and when they join mid-round, and it is what
 * `spectator` will specialise from in M4 when a found hider has to watch.
 */
export const Team = {
  Unassigned: 0,
  Hunter: 1,
  Hider: 2,
} as const;

export type TeamId = (typeof Team)[keyof typeof Team];

export function isPlayingTeam(team: number): boolean {
  return team === Team.Hunter || team === Team.Hider;
}

export function opposingTeam(team: TeamId): TeamId {
  if (team === Team.Hunter) return Team.Hider;
  if (team === Team.Hider) return Team.Hunter;
  return Team.Unassigned;
}

export function teamLabel(team: number): string {
  switch (team) {
    case Team.Hunter: return "hunter";
    case Team.Hider: return "hider";
    default: return "unassigned";
  }
}

// ---------------------------------------------------------------------------
// Phases
// ---------------------------------------------------------------------------

export const RoundPhase = {
  Warmup: 0,
  Hiding: 1,
  Hunting: 2,
  Over: 3,
} as const;

export type RoundPhaseId = (typeof RoundPhase)[keyof typeof RoundPhase];

export function phaseLabel(phase: number): string {
  switch (phase) {
    case RoundPhase.Warmup: return "warmup";
    case RoundPhase.Hiding: return "hiding";
    case RoundPhase.Hunting: return "hunting";
    case RoundPhase.Over: return "over";
    default: return "unknown";
  }
}

const seconds = (s: number): number => Math.round(s * TICK_RATE);

/**
 * Phase durations, in ticks.
 *
 * Warmup has no duration: it ends on a decision — the host's, or the
 * auto-start's — rather than when a timer measured from its own start expires,
 * so it is the one phase whose exit is a condition rather than a deadline. It
 * has a countdown in it and still does not belong here; see
 * `WARMUP_AUTOSTART_TICKS`.
 */
export const HIDING_TICKS = seconds(30);
export const HUNTING_TICKS = seconds(240);
export const OVER_TICKS = seconds(8);

/**
 * Fewest connected players for a round to start.
 *
 * Two is the real minimum — one hunter and one hider. Below that there is
 * nothing to hunt, and a "round" with a single player is a lobby with a
 * countdown in it.
 */
export const MIN_PLAYERS_TO_START = 2;

/**
 * How long a lobby at quorum waits for its host before starting itself.
 *
 * The host's START is the real path out of Warmup; this is the fallback that
 * stops one idle person from holding a room hostage. It lives here beside the
 * phase durations rather than in rules.ts because it is a fact about the round
 * lifecycle, and it is deliberately *not* a `phaseDurationTicks` entry — see
 * `nextPhase` and `roundStateAtTick` for why Warmup must stay underivable.
 *
 * Measured from the tick the lobby reached `MIN_PLAYERS_TO_START`, not from the
 * tick Warmup began. A lobby that sat empty for ten minutes would otherwise
 * start the instant a second player walked in, which is the behaviour this
 * whole mechanism exists to remove.
 */
export const WARMUP_AUTOSTART_TICKS = seconds(45);

/**
 * Share of players who hunt, rounded down, floored at one.
 *
 * Prop hunt is asymmetric on purpose: hiders outnumber hunters, because being
 * found is quick and searching is slow. A third is the traditional split. The
 * floor matters at exactly two players, where a third of two rounds to zero and
 * the round would otherwise start with nobody hunting.
 */
export const HUNTER_FRACTION = 1 / 3;

export function hunterCountFor(playerCount: number): number {
  if (playerCount < MIN_PLAYERS_TO_START) return 0;
  return Math.max(1, Math.floor(playerCount * HUNTER_FRACTION));
}

/** Ticks a phase lasts, or `null` for a phase that ends on a condition. */
export function phaseDurationTicks(phase: RoundPhaseId): number | null {
  switch (phase) {
    case RoundPhase.Hiding: return HIDING_TICKS;
    case RoundPhase.Hunting: return HUNTING_TICKS;
    case RoundPhase.Over: return OVER_TICKS;
    default: return null;
  }
}

/**
 * May this team move right now?
 *
 * The frozen hunter during Hiding is the load-bearing rule of the whole game,
 * so it lives here as data rather than as an `if` somewhere in the tick loop —
 * the server consults it to decide whether to apply input, and the client
 * consults the same function to grey out the HUD. One definition, two readers,
 * exactly like `movement.ts`.
 *
 * Note this governs *movement*, not looking: a frozen hunter can still turn on
 * the spot. Taking the camera away reads as a broken game rather than a rule.
 */
export function canMove(phase: RoundPhaseId, team: TeamId, found: boolean): boolean {
  // A found hider is spectating. Frozen rather than removed: they still hold a
  // socket, still receive snapshots, and still need to be simulated so gravity
  // settles them — they simply stop being a participant. `found` is a required
  // parameter rather than an optional one precisely because both sides call
  // this and a default would let one of them quietly forget.
  if (found) return false;
  if (phase === RoundPhase.Warmup) return true;
  if (phase === RoundPhase.Over) return false;
  if (phase === RoundPhase.Hiding) return team !== Team.Hunter;
  return true;
}

/**
 * The phase in effect at `tick`, derived rather than waited for.
 *
 * `canMove` answers "may this team move in this phase"; this answers "which
 * phase is it", and the two are only useful together. The client used to gate
 * on the phase it had last been *told* — the phase in the newest `Msg.Round` —
 * which arrives ~RTT/2 after the server has already switched. For that window
 * the two sides stripped different bits from the same input frames, diverging
 * by HUNTER_SPEED * TICK_DT (0.25m) every tick, and reconciliation could not
 * recover it: the client replays its pending frames through the same `step()`,
 * so it reproduces its own gating, not the server's. That is the 0.25m median
 * error at phase transitions.
 *
 * Deriving the phase from `phaseStartedTick` plus the durations above closes
 * the window, but only for the transitions that are actually deadlines. The
 * conditional ones cannot be derived, because they depend on facts about the
 * room that only the server holds:
 *
 *   Hiding  -> Hunting   deadline, unconditional in `nextPhase`. EXACT.
 *   Hunting -> Over      deadline, but can also fire early when a side is
 *                        emptied, so a derived phase may lag the server here.
 *   Over    -> Hiding    conditional on `playerCount`. NOT derivable.
 *   Warmup  -> Hiding    conditional on `playerCount`, on the host having
 *                        pressed START, and on a countdown measured from a tick
 *                        that is not `phaseStartedTick`. NOT derivable, and the
 *                        auto-start does not make it so — which is exactly why
 *                        WARMUP_AUTOSTART_TICKS is not a `phaseDurationTicks`
 *                        entry. The lobby HUD shows that countdown from
 *                        `RoundMsg.startsAtTick`, where it gates nothing.
 *
 * So this rolls forward across the two deadline exits and stops at the two
 * conditional ones, leaving the caller in the last phase it can honestly claim
 * until a `Msg.Round` settles it. That bias is deliberate: both stopping points
 * leave a client in a phase where `canMove` is more restrictive than the
 * server's, and `Over -> Hiding` respawns everyone anyway, which snaps
 * regardless of what the client predicted. The alternative — guessing at player
 * counts to derive the conditional legs — diverges *silently* when the guess is
 * wrong, which is strictly worse than the bug this replaces.
 *
 * The server calls this too, where it is a no-op: `doTick` transitions before
 * it applies input, so `phaseAtTick(this.round, this.tick)` is already
 * `this.round.phase`. It calls it anyway so both gate call sites read
 * identically and neither can drift from the other by being edited alone.
 */
export function phaseAtTick(state: RoundState, tick: number): RoundPhaseId {
  return roundStateAtTick(state, tick).phase;
}

/**
 * `phaseAtTick`, but carrying the derived `phaseStartedTick` with it.
 *
 * Anything computing a *countdown* needs this rather than the bare phase:
 * `ticksRemaining` measures from `phaseStartedTick`, so pairing a rolled-forward
 * phase with the original start tick would report the new phase as having
 * already expired. The HUD reads this so its countdown and its banner describe
 * the same phase.
 *
 * Returns `state` itself when nothing has rolled, which is the overwhelmingly
 * common case — no allocation on a per-frame path unless a deadline has
 * actually passed.
 */
export function roundStateAtTick(state: RoundState, tick: number): RoundState {
  let phase = state.phase;
  let startedTick = state.phaseStartedTick;

  // Bounded by the number of deadline phases, not by elapsed time: Hiding can
  // only roll into Hunting and Hunting only into Over, where it stops.
  for (;;) {
    const duration = phaseDurationTicks(phase);
    if (duration === null) break;
    if (tick - startedTick < duration) break;

    if (phase === RoundPhase.Hiding) {
      phase = RoundPhase.Hunting;
    } else if (phase === RoundPhase.Hunting) {
      phase = RoundPhase.Over;
    } else {
      // Over. Its successor depends on `playerCount`; hold here.
      break;
    }
    startedTick += duration;
  }

  if (phase === state.phase) return state;
  return { ...state, phase, phaseStartedTick: startedTick };
}

/** The bits `gateInput` strips. Look, jump and crouch are not locomotion. */
const MOVEMENT_BUTTONS = Btn.Forward | Btn.Back | Btn.Left | Btn.Right | Btn.Jump;

/**
 * An input frame with movement removed if the player is held.
 *
 * This lives in shared, beside `canMove`, because **both sides must apply it**.
 * The server enforcing the freeze alone is not enough: the client predicts with
 * the same `step()` and would walk a held hunter forward while the server kept
 * them still, diverging by a full tick of movement every tick until the error
 * blew past RECONCILE_SNAP_DISTANCE and hard-snapped. Prediction only works
 * when both ends simulate identical input, which is the same reason the raw
 * mouse angles are quantised before being predicted with.
 *
 * Yaw and pitch pass through untouched — `canMove` governs movement, not
 * looking. Note also that the frame is still *simulated*: a held player runs
 * `step()` with no movement bits, so gravity still applies and a hunter frozen
 * in mid-air still lands.
 */
export function gateInput(frame: InputFrame, allowed: boolean): InputFrame {
  if (allowed) return frame;
  return { ...frame, buttons: gateButtons(frame.buttons, allowed) };
}

/**
 * `gateInput` for a bare button bitfield.
 *
 * The client gates at the point it samples input, before it has built a frame
 * at all; the server gates a frame it has already decoded. Same rule, two
 * shapes, one implementation.
 */
export function gateButtons(buttons: number, allowed: boolean): number {
  return allowed ? buttons : buttons & ~MOVEMENT_BUTTONS;
}

// ---------------------------------------------------------------------------
// Round state
// ---------------------------------------------------------------------------

/**
 * Everything a client needs to render the round, and everything the server
 * needs to advance it. Small enough to sit in every snapshot header, but it is
 * sent as its own message instead — see the lessons doc: state snapshots are
 * safe to drop because a newer one supersedes them, whereas a phase change is
 * a discrete event, and folding an event into a droppable packet is how you
 * lose it.
 */
export interface RoundState {
  phase: RoundPhaseId;
  /** Tick the current phase began. Elapsed time is `tick - phaseStartedTick`. */
  phaseStartedTick: number;
  /** Rounds completed this session; drives the team swap. */
  roundNumber: number;
  huntersWon: number;
  hidersWon: number;
}

export function initialRoundState(): RoundState {
  return {
    phase: RoundPhase.Warmup,
    phaseStartedTick: 0,
    roundNumber: 0,
    huntersWon: 0,
    hidersWon: 0,
  };
}

/** Ticks left in the current phase, or `null` if it has no deadline. */
export function ticksRemaining(state: RoundState, tick: number): number | null {
  const duration = phaseDurationTicks(state.phase);
  if (duration === null) return null;
  const elapsed = tick - state.phaseStartedTick;
  return elapsed >= duration ? 0 : duration - elapsed;
}

/** Seconds left, for a HUD. Rounded up so the last second is shown as 1, not 0. */
export function secondsRemaining(state: RoundState, tick: number): number | null {
  const ticks = ticksRemaining(state, tick);
  return ticks === null ? null : Math.ceil(ticks / TICK_RATE);
}

/**
 * Why a round ended. `Timeout` is a hider win: surviving the clock is the
 * hiders' win condition, not a draw.
 */
export const RoundOutcome = {
  None: 0,
  HuntersFoundEveryone: 1,
  Timeout: 2,
  /** Everyone on a side disconnected. Ends the round without crediting a win. */
  Abandoned: 3,
} as const;

export type RoundOutcomeId = (typeof RoundOutcome)[keyof typeof RoundOutcome];

export function winnerOf(outcome: RoundOutcomeId): TeamId {
  switch (outcome) {
    case RoundOutcome.HuntersFoundEveryone: return Team.Hunter;
    case RoundOutcome.Timeout: return Team.Hider;
    default: return Team.Unassigned;
  }
}

/**
 * Which team a player takes next round.
 *
 * A straight swap rather than a reshuffle: the point of swapping is that the
 * same people experience both sides of the same map, and re-randomising throws
 * that away for a third of the room every round.
 */
export function teamAfterSwap(team: TeamId): TeamId {
  return opposingTeam(team);
}

/**
 * The next phase, given the current one and what is true in the world.
 *
 * A pure transition function, deliberately: it is the entire round lifecycle in
 * one place that can be exhaustively tested without a Durable Object, a socket
 * or a clock. The caller owns the side effects — assigning teams, moving
 * players to spawns, sending messages — and this owns only the decision.
 *
 * Returns `null` to stay in the current phase.
 */
export function nextPhase(
  state: RoundState,
  tick: number,
  world: {
    playerCount: number;
    /** Hiders not yet found. Only meaningful during Hunting. */
    hidersRemaining: number;
    huntersRemaining: number;
    /**
     * The host has asked to start. Only meaningful during Warmup.
     *
     * A request rather than a command: it arrives on a socket, and what it
     * means is decided here with everything else. The caller owns *whether the
     * sender was allowed to ask* — being the host, in a lobby at quorum — for
     * the same reason `resolveTag` owns geometry and not eligibility.
     */
    startRequested: boolean;
    /**
     * Tick the lobby reached `MIN_PLAYERS_TO_START`, or `null` if it has not.
     *
     * Not `phaseStartedTick`: see `WARMUP_AUTOSTART_TICKS`.
     */
    quorumSinceTick: number | null;
  },
): RoundPhaseId | null {
  const expired = (): boolean => {
    const left = ticksRemaining(state, tick);
    return left !== null && left <= 0;
  };

  switch (state.phase) {
    case RoundPhase.Warmup: {
      // Quorum first, and it outranks both of the ways out below: a start
      // requested by a host whose lobby then emptied must not fire when the
      // room refills, and a countdown that began at quorum stops meaning
      // anything the moment quorum is lost.
      if (world.playerCount < MIN_PLAYERS_TO_START) return null;
      if (world.startRequested) return RoundPhase.Hiding;
      if (world.quorumSinceTick === null) return null;
      // The fallback. A lobby whose host has walked away still plays.
      return tick - world.quorumSinceTick >= WARMUP_AUTOSTART_TICKS
        ? RoundPhase.Hiding
        : null;
    }

    case RoundPhase.Hiding:
      // Dropping below the minimum mid-setup returns to Warmup rather than
      // starting a round that cannot be played.
      if (world.playerCount < MIN_PLAYERS_TO_START) return RoundPhase.Warmup;
      return expired() ? RoundPhase.Hunting : null;

    case RoundPhase.Hunting:
      if (world.hidersRemaining <= 0 || world.huntersRemaining <= 0) return RoundPhase.Over;
      return expired() ? RoundPhase.Over : null;

    case RoundPhase.Over:
      if (!expired()) return null;
      return world.playerCount >= MIN_PLAYERS_TO_START ? RoundPhase.Hiding : RoundPhase.Warmup;

    default:
      return null;
  }
}

/**
 * How a round that is ending should be scored.
 *
 * Separate from `nextPhase` because the transition into `Over` and the reason
 * for it are different questions, and only one of them affects the scoreboard.
 */
export function outcomeFor(world: {
  hidersRemaining: number;
  huntersRemaining: number;
  /**
   * Hiders still connected, found or not.
   *
   * Without this, "no hiders left" cannot distinguish being hunted down from
   * everyone quitting, and the hunters are credited with a win for an empty
   * room. It only became a real distinction when being found became a real
   * way to leave the count — before M4, a hider left it only by
   * disconnecting, so the two cases were the same case.
   */
  hidersConnected: number;
}): RoundOutcomeId {
  if (world.huntersRemaining <= 0) return RoundOutcome.Abandoned;
  if (world.hidersConnected <= 0) return RoundOutcome.Abandoned;
  if (world.hidersRemaining <= 0) return RoundOutcome.HuntersFoundEveryone;
  return RoundOutcome.Timeout;
}
